/**
 * Service de gestion d'erreurs unifié
 * Conformément aux exigences 4.3 et 7.3 pour la cohérence de la gestion d'erreurs
 */

import { PostgrestError } from '@supabase/supabase-js';
import { 
  BusinessError, 
  ValidationError, 
  StateTransitionError,
  BusinessRuleViolationError,
  PermissionDeniedError,
  ResourceNotFoundError,
  DataConflictError,
  ExternalServiceError,
  isBusinessError
} from '../errors/BusinessErrors';
// Import logger with fallback
let logger: { error: (msg: string, ...args: unknown[]) => void; warn: (msg: string, ...args: unknown[]) => void; info: (msg: string, ...args: unknown[]) => void; };
try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  logger = require('../utils/logger').logger;
} catch {
  // Fallback logger if the module doesn't exist
  logger = {
    error: console.error,
    warn: console.warn,
    info: (...args: any[]) => console.log(...args)
  };
}

/**
 * Interface pour les erreurs conviviales affichées à l'utilisateur
 */
export interface UserFriendlyError {
  message: string;
  code: string;
  severity: 'error' | 'warning' | 'info';
  field?: string;
  actions?: string[];
}

/**
 * Interface pour le contexte d'erreur
 */
export interface ErrorContext {
  component?: string;
  function?: string;
  userId?: string;
  campaignId?: string;
  referentId?: string;
  operation?: string;
  [key: string]: unknown;
}

/**
 * Service principal de gestion d'erreurs unifié
 * Centralise la traduction et la gestion de tous les types d'erreurs
 */
export class ErrorHandler {
  /**
   * Gère une erreur et retourne une version conviviale pour l'utilisateur
   */
  static handle(error: Error | BusinessError, context?: ErrorContext): UserFriendlyError {
    // Log l'erreur avec le contexte
    this.logError(error, context);

    // Si c'est déjà une erreur métier, la traiter directement
    if (isBusinessError(error)) {
      return this.handleBusinessError(error);
    }

    // Gestion des erreurs PostgreSQL/Supabase
    if (this.isPostgresError(error)) {
      return this.handlePostgresError(error as PostgrestError);
    }

    // Gestion des erreurs réseau
    if (this.isNetworkError(error)) {
      return this.handleNetworkError(error);
    }

    // Gestion des erreurs de validation Zod
    if (this.isZodError(error)) {
      return this.handleZodError(error);
    }

    // Erreur générique
    return this.handleGenericError(error);
  }

  /**
   * Gère les erreurs métier avec messages traduits
   */
  private static handleBusinessError(error: BusinessError): UserFriendlyError {
    const baseError: UserFriendlyError = {
      message: error.userMessage,
      code: error.code,
      severity: this.getSeverityForBusinessError(error),
      field: error.field
    };

    // Ajouter des actions de récupération spécifiques
    baseError.actions = this.getRecoveryActions(error);

    return baseError;
  }

  /**
   * Gère les erreurs PostgreSQL avec traduction en français
   */
  private static handlePostgresError(error: PostgrestError): UserFriendlyError {
    const code = error.code;
    const message = error.message?.toLowerCase() || '';

    // Erreurs de contrainte d'unicité (23505)
    if (code === '23505' || message.includes('duplicate key')) {
      return {
        message: 'Cette donnée existe déjà dans le système',
        code: 'DUPLICATE_DATA',
        severity: 'warning',
        actions: [
          'Vérifiez si la donnée n\'existe pas déjà',
          'Modifiez les informations pour les rendre uniques'
        ]
      };
    }

    // Erreurs de clé étrangère (23503)
    if (code === '23503' || message.includes('foreign key')) {
      return {
        message: 'Cette action ne peut pas être effectuée car des données liées existent',
        code: 'REFERENCED_DATA',
        severity: 'warning',
        actions: [
          'Supprimez d\'abord les données liées',
          'Contactez l\'administrateur si nécessaire'
        ]
      };
    }

    // Erreurs de contrainte de vérification (23514)
    if (code === '23514' || message.includes('check constraint')) {
      return {
        message: 'Les données ne respectent pas les règles de validation',
        code: 'CONSTRAINT_VIOLATION',
        severity: 'warning',
        actions: [
          'Vérifiez que les données respectent les règles requises',
          'Consultez la documentation pour les formats acceptés'
        ]
      };
    }

    // Erreurs de permissions (42501)
    if (code === '42501' || message.includes('permission denied')) {
      return {
        message: 'Vous n\'avez pas les permissions nécessaires pour cette action',
        code: 'PERMISSION_DENIED',
        severity: 'error',
        actions: [
          'Contactez votre administrateur',
          'Vérifiez que vous êtes connecté avec le bon compte'
        ]
      };
    }

    // Erreurs RLS (Row Level Security)
    if (message.includes('row level security') || message.includes('policy')) {
      return {
        message: 'Accès refusé à cette ressource',
        code: 'ACCESS_DENIED',
        severity: 'error',
        actions: [
          'Vérifiez que vous avez accès à cette ressource',
          'Contactez l\'administrateur si le problème persiste'
        ]
      };
    }

    // Erreurs de timeout
    if (message.includes('timeout') || message.includes('connection')) {
      return {
        message: 'La connexion à la base de données a échoué',
        code: 'DATABASE_CONNECTION_ERROR',
        severity: 'error',
        actions: [
          'Vérifiez votre connexion internet',
          'Réessayez dans quelques instants',
          'Contactez le support si le problème persiste'
        ]
      };
    }

    // Erreur PostgreSQL générique
    return {
      message: 'Erreur de base de données. Veuillez réessayer.',
      code: 'DATABASE_ERROR',
      severity: 'error',
      actions: [
        'Réessayez votre action',
        'Vérifiez vos données',
        'Contactez le support si le problème persiste'
      ]
    };
  }

  /**
   * Gère les erreurs réseau
   */
  private static handleNetworkError(_error: Error): UserFriendlyError {
    return {
      message: 'Problème de connexion réseau',
      code: 'NETWORK_ERROR',
      severity: 'error',
      actions: [
        'Vérifiez votre connexion internet',
        'Réessayez dans quelques instants',
        'Contactez le support si le problème persiste'
      ]
    };
  }

  /**
   * Gère les erreurs de validation Zod
   */
  private static handleZodError(error: { issues?: Array<{ path?: string[]; message: string }> }): UserFriendlyError {
    const issues = error.issues || [];
    const firstIssue = issues[0];
    
    if (firstIssue) {
      const field = firstIssue.path?.join('.') || 'données';
      const translatedMessage = this.translateZodMessage(firstIssue.message, field);
      
      return {
        message: translatedMessage,
        code: 'VALIDATION_ERROR',
        severity: 'warning',
        field: field,
        actions: [
          'Vérifiez les informations saisies',
          'Assurez-vous que tous les champs requis sont remplis'
        ]
      };
    }

    return {
      message: 'Les données fournies ne sont pas valides',
      code: 'VALIDATION_ERROR',
      severity: 'warning',
      actions: [
        'Vérifiez les informations saisies',
        'Assurez-vous que tous les champs requis sont remplis'
      ]
    };
  }

  /**
   * Gère les erreurs génériques
   */
  private static handleGenericError(_error: Error): UserFriendlyError {
    return {
      message: 'Une erreur inattendue s\'est produite',
      code: 'UNKNOWN_ERROR',
      severity: 'error',
      actions: [
        'Rafraîchissez la page',
        'Réessayez votre action',
        'Contactez le support si le problème persiste'
      ]
    };
  }

  /**
   * Traduit les messages d'erreur Zod en français
   */
  private static translateZodMessage(message: string, field: string): string {
    const translations: Record<string, string> = {
      'Required': `Le champ ${field} est obligatoire`,
      'String must contain at least 1 character(s)': `Le champ ${field} ne peut pas être vide`,
      'String must contain at least 5 character(s)': `Le champ ${field} doit contenir au moins 5 caractères`,
      'String must contain at most 200 character(s)': `Le champ ${field} ne peut pas dépasser 200 caractères`,
      'Array must contain at least 5 element(s)': 'Au moins 5 critères sont requis',
      'Array must contain at most 10 element(s)': 'Maximum 10 critères autorisés',
      'Expected number, received string': `Le champ ${field} doit être un nombre`,
      'Expected string, received number': `Le champ ${field} doit être du texte`,
      'Invalid email': `L'adresse email n'est pas valide`,
      'Invalid input': `Le format du champ ${field} n'est pas valide`
    };

    return translations[message] || `Erreur de validation pour le champ ${field}`;
  }

  /**
   * Détermine la sévérité d'une erreur métier
   */
  private static getSeverityForBusinessError(error: BusinessError): 'error' | 'warning' | 'info' {
    if (error instanceof ValidationError) {
      return 'warning';
    }
    
    if (error instanceof ResourceNotFoundError) {
      return 'info';
    }
    
    if (error instanceof PermissionDeniedError || error instanceof StateTransitionError) {
      return 'error';
    }
    
    if (error instanceof DataConflictError) {
      return 'warning';
    }
    
    return 'error';
  }

  /**
   * Retourne les actions de récupération pour une erreur métier
   */
  private static getRecoveryActions(error: BusinessError): string[] {
    if (error instanceof ValidationError) {
      return [
        'Vérifiez les informations saisies',
        'Assurez-vous que tous les champs requis sont remplis'
      ];
    }
    
    if (error instanceof StateTransitionError) {
      return [
        'Vérifiez l\'état actuel de la ressource',
        'Contactez l\'administrateur si nécessaire'
      ];
    }
    
    if (error instanceof PermissionDeniedError) {
      return [
        'Contactez votre administrateur',
        'Vérifiez que vous avez les bonnes permissions'
      ];
    }
    
    if (error instanceof ResourceNotFoundError) {
      return [
        'Vérifiez que la ressource existe',
        'Actualisez la page et réessayez'
      ];
    }
    
    if (error instanceof DataConflictError) {
      return [
        'Actualisez les données et réessayez',
        'Vérifiez qu\'aucune modification concurrente n\'a eu lieu'
      ];
    }
    
    return [
      'Réessayez votre action',
      'Contactez le support si le problème persiste'
    ];
  }

  /**
   * Vérifie si une erreur est une erreur PostgreSQL
   */
  private static isPostgresError(error: unknown): boolean {
    return error?.name === 'PostgrestError' || 
           error?.code?.startsWith('PGRST') ||
           error?.code?.match(/^\d{5}$/); // Codes d'erreur PostgreSQL (5 chiffres)
  }

  /**
   * Vérifie si une erreur est une erreur réseau
   */
  private static isNetworkError(error: unknown): boolean {
    return error?.name === 'NetworkError' ||
           error?.code === 'NETWORK_ERROR' ||
           error?.message?.includes('fetch') ||
           error?.message?.includes('network') ||
           error?.message?.includes('connection refused');
  }

  /**
   * Vérifie si une erreur est une erreur de validation Zod
   */
  private static isZodError(error: unknown): error is { name?: string; issues?: Array<{ path?: string[]; message: string }> } {
    return (error as any)?.name === 'ZodError' || 
           ((error as any)?.issues && Array.isArray((error as any).issues));
  }

  /**
   * Log une erreur avec son contexte
   */
  private static logError(error: Error, context?: ErrorContext): void {
    const logContext = {
      errorName: error.name,
      errorMessage: error.message,
      stack: error.stack,
      ...context,
      timestamp: new Date().toISOString()
    };

    if (isBusinessError(error)) {
      // Log des erreurs métier avec niveau approprié
      if (error instanceof ValidationError) {
        logger.warn(`Erreur de validation: ${error.message}`, logContext);
      } else if (error instanceof PermissionDeniedError) {
        logger.error(`Erreur de permission: ${error.message}`, logContext);
      } else {
        logger.info(`Erreur métier: ${error.message}`, logContext);
      }
    } else {
      // Log des erreurs techniques
      logger.error(`Erreur technique: ${error.message}`, logContext, error);
    }
  }

  /**
   * Gère les erreurs de base de données et les convertit en erreurs métier
   */
  static handleDatabaseError(error: PostgrestError, context?: ErrorContext): never {
    const businessError = this.createBusinessErrorFromPostgres(error, context);
    throw businessError;
  }

  /**
   * Gère les erreurs des Edge Functions Supabase
   */
  static handleEdgeFunctionError(error: { message?: string; status?: number }): never {
    const message = error.message || '';
    const status = error.status || 500;
    
    // Gestion spécifique des erreurs 400 (Bad Request)
    if (status === 400) {
      if (message.includes('campaign_token') || message.includes('Invalid campaign token')) {
        throw new ValidationError('Token de campagne invalide ou expiré');
      }
      if (message.includes('referents') && message.includes('required')) {
        throw new ValidationError('Données des référents manquantes ou invalides');
      }
      if (message.includes('already submitted')) {
        throw new DataConflictError('soumission', 'Référents déjà soumis pour cette campagne');
      }
      if (message.includes('email')) {
        throw new ValidationError('Format d\'email invalide détecté');
      }
      // Erreur 400 générique
      throw new ValidationError(`Données invalides: ${message || 'Vérifiez vos informations'}`);
    }
    
    // Gestion des erreurs 401 (Unauthorized)
    if (status === 401) {
      throw new PermissionDeniedError('soumission', 'campagne', undefined);
    }
    
    // Gestion des erreurs 404 (Not Found)
    if (status === 404) {
      throw new ValidationError('Campagne introuvable ou expirée');
    }
    
    // Gestion des erreurs 500+ (Server Error)
    if (status >= 500) {
      throw new ExternalServiceError(
        'edge_function',
        'Erreur technique du serveur, réessayez dans quelques minutes'
      );
    }
    
    // Autres erreurs
    throw new ExternalServiceError(
      'edge_function',
      `Erreur lors de l'opération (${status}): ${message || 'Erreur inconnue'}`
    );
  }

  /**
   * Crée une erreur métier à partir d'une erreur PostgreSQL
   */
  static createBusinessErrorFromPostgres(
    error: PostgrestError, 
    context?: ErrorContext
  ): BusinessError {
    const code = error.code;
    const message = error.message?.toLowerCase() || '';

    if (code === '23505' || message.includes('duplicate key')) {
      return new DataConflictError(
        'création',
        'Une donnée avec ces informations existe déjà',
        context
      );
    }

    if (code === '23503' || message.includes('foreign key')) {
      return new BusinessRuleViolationError(
        'intégrité_référentielle',
        'Cette action violerait l\'intégrité des données',
        context
      );
    }

    if (code === '42501' || message.includes('permission denied')) {
      return new PermissionDeniedError(
        context?.operation || 'action',
        context?.campaignId || context?.referentId || 'ressource',
        context?.userId,
        context
      );
    }

    // Erreur générique pour les autres cas
    return new ExternalServiceError(
      'base_de_données',
      error.message || 'Erreur de base de données',
      context
    );
  }
}

/**
 * Hook React pour utiliser le gestionnaire d'erreurs
 */
export const useErrorHandler = () => {
  const handleError = (error: Error | BusinessError, context?: ErrorContext): UserFriendlyError => {
    return ErrorHandler.handle(error, context);
  };

  const createBusinessError = (error: PostgrestError, context?: ErrorContext): BusinessError => {
    return ErrorHandler.createBusinessErrorFromPostgres(error, context);
  };

  return {
    handleError,
    createBusinessError
  };
};

/**
 * Utilitaire pour wrapper les fonctions avec gestion d'erreurs automatique
 */
export const withErrorHandling = <T extends unknown[], R>(
  fn: (...args: T) => Promise<R>,
  context?: ErrorContext
) => {
  return async (...args: T): Promise<R> => {
    try {
      return await fn(...args);
    } catch (_error) {
      const _handledError = ErrorHandler.handle(error as Error, context);
      
      // Re-lancer l'erreur originale pour que l'appelant puisse la gérer
      throw error;
    }
  };
};